import React, { useState, useEffect } from 'react';
import moment from "moment-timezone";
import { useToast } from "@/components/ui/use-toast";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { runReport } from '@/lib/gfReport';
import { CommonDestinationTimezone } from "@/lib/utils";

export function GFReportDashboard() {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [CADtoUSDrate, setCADtoUSDrate] = useState(1);
  const [monthAName, setMonthAName] = useState(moment.tz(CommonDestinationTimezone).format("MMMM"));
  const [monthBName, setMonthBName] = useState(moment.tz(CommonDestinationTimezone).subtract(1, 'month').format("MMMM"));
  const [files, setFiles] = useState({
    gfFile: null as File | null,
    gfFile2: null as File | null,
    monthA: {
      unsettledApUs: null as File | null,
      unsettledApCan: null as File | null,
      unsettledArCan: null as File | null,
      unsettledArUs: null as File | null,
    },
    monthB: {
      unsettledApUs: null as File | null,
      unsettledApCan: null as File | null,
      unsettledArCan: null as File | null,
      unsettledArUs: null as File | null,
    }
  });

  useEffect(() => {
    const allFiles = [
      ...Object.values(files.monthA).filter(Boolean),
      ...Object.values(files.monthB).filter(Boolean)
    ];

    if (allFiles.length > 0) {
      const fileNames = allFiles.map(f => f?.name || '');
      const monthPattern = /(?:january|february|march|april|may|june|july|august|september|october|november|december)/i;

      const months = fileNames.map(name => {
        const match = name.match(monthPattern);
        return match ? match[0].charAt(0).toUpperCase() + match[0].slice(1).toLowerCase() : null;
      }).filter(Boolean);
        /*
        * const uniqueMonths = [...new Set(months)];
        * if (uniqueMonths.length >= 2) {
        *   setMonthAName(uniqueMonths[0] || '');
        *   setMonthBName(uniqueMonths[1] || '');
        * } else if (uniqueMonths.length === 1) {
        *   setMonthAName(uniqueMonths[0] || '');
        * } */
    }
  }, [files]);

  const handleFileChange = (category: 'gfFile' | 'gfFile2' | 'monthA' | 'monthB', subCategory?: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (category === 'gfFile') {
      setFiles(prev => ({ ...prev, gfFile: file }));
    } else if (category === 'gfFile2') {
      setFiles(prev => ({ ...prev, gfFile2: file }));
    } else if (subCategory) {
      setFiles(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [subCategory]: file
        }
      }));
    }
  };

  const handleGenerate = async () => {
    if (!files.gfFile) {
      return;
    } else if (isGenerating) { return; }

    setIsGenerating(true);
    try {
      const result = await runReport({
        month_a: {
          unsettled_ap_us: files.monthA.unsettledApUs || undefined,
          unsettled_ap_can: files.monthA.unsettledApCan || undefined,
          unsettled_ar_can: files.monthA.unsettledArCan || undefined,
          unsettled_ar_us: files.monthA.unsettledArUs || undefined,
          name: monthAName
        },
        month_b: {
          unsettled_ap_us: files.monthB.unsettledApUs || undefined,
          unsettled_ap_can: files.monthB.unsettledApCan || undefined,
          unsettled_ar_can: files.monthB.unsettledArCan || undefined,
          unsettled_ar_us: files.monthB.unsettledArUs || undefined,
          name: monthBName
        },
        gf_file: files.gfFile2 ? [files.gfFile, files.gfFile2] : [files.gfFile],
        CADtoUSD_rate: CADtoUSDrate,
      });

      const blob = new Blob([result.buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GF_Charges_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);

      toast({
        title: "Generated"
      })
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: "Failed to generate report"
      })
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle>GF Charges Report</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="gf-charges">GF Charges File (Required)</Label>
              <Input
                id="gf-charges"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange('gfFile')}
                disabled={isGenerating}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gf-charges2">GF Charges File (Optional)</Label>
              <Input
                id="gf-charges2"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange('gfFile2')}
                disabled={isGenerating}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="month-a-name">CAD to USD rate (1 CAD = ___ USD)</Label>
            <Input
              id="cad-to-usd-rate"
              type="number"
              value={CADtoUSDrate}
              onChange={(e) => setCADtoUSDrate(e.target.value.trim() === "" ? 1 : Number(e.target.value))}
              disabled={isGenerating}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Current Month</h3>
              <div className="space-y-2">
                <Label htmlFor="month-a-name">Month Name</Label>
                <Input
                  id="month-a-name"
                  type="text"
                  value={monthAName}
                  onChange={(e) => setMonthAName(e.target.value)}
                  placeholder="e.g., June"
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="month-a-ap-us">Unsettled AP US</Label>
                <Input
                  id="month-a-ap-us"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange('monthA', 'unsettledApUs')}
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="month-a-ap-can">Unsettled AP CAN</Label>
                <Input
                  id="month-a-ap-can"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange('monthA', 'unsettledApCan')}
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="month-a-ar-can">Unsettled AR CAN</Label>
                <Input
                  id="month-a-ar-can"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange('monthA', 'unsettledArCan')}
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="month-a-ar-us">Unsettled AR US</Label>
                <Input
                  id="month-a-ar-us"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange('monthA', 'unsettledArUs')}
                  disabled={isGenerating}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Previous Month</h3>
              <div className="space-y-2">
                <Label htmlFor="month-b-name">Month Name</Label>
                <Input
                  id="month-b-name"
                  type="text"
                  value={monthBName}
                  onChange={(e) => setMonthBName(e.target.value)}
                  placeholder="e.g., May"
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="month-b-ap-us">Unsettled AP US</Label>
                <Input
                  id="month-b-ap-us"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange('monthB', 'unsettledApUs')}
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="month-b-ap-can">Unsettled AP CAN</Label>
                <Input
                  id="month-b-ap-can"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange('monthB', 'unsettledApCan')}
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="month-b-ar-can">Unsettled AR CAN</Label>
                <Input
                  id="month-b-ar-can"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange('monthB', 'unsettledArCan')}
                  disabled={isGenerating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="month-b-ar-us">Unsettled AR US</Label>
                <Input
                  id="month-b-ar-us"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange('monthB', 'unsettledArUs')}
                  disabled={isGenerating}
                />
              </div>
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={!files.gfFile || isGenerating}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Report...
              </>
            ) : (
              'Generate Report'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default GFReportDashboard;
